import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  usePropertyField,
  useContentToken,
  EditableToken,
  RichTextContent,
  useSlotGroup,
  EditableSlotGroup,
} from '@vibesites/runtime/react';

const contactLinkDef = {
  label: 'Contact Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/contact-us' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Contact Us' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

export function AccessibilityStatement() {
  const heading = useContentToken('accessibilitystatement.heading');
  const domain = usePropertyField('property_domain') as string || '';

  const iframeSrc = domain
    ? `https://portal.audioeye.com/accessibility-statement.html?domain=${encodeURIComponent(domain)}`
    : '';

  const contactLink = useSlotGroup('accessibilitystatement.contact_link', contactLinkDef);

  return (
    <section className="bg-white py-8 md:py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <EditableToken
          path="accessibilitystatement.heading"
          as="h1"
          className="sr-only"
        >
          {heading}
        </EditableToken>

        <RichTextContent
          path="accessibilitystatement.body"
          label="Accessibility Statement"
          as="div"
          className="font-body text-base leading-relaxed text-muted-foreground [&_p]:mb-4 [&_a]:text-primary [&_a]:underline mb-8"
        />

        {iframeSrc && (
          <iframe
            src={iframeSrc}
            title="Accessibility Statement"
            className="w-full border-0"
            style={{ height: '2300px' }}
          />
        )}

        <EditableSlotGroup
          groupPath="accessibilitystatement.contact_link"
          definition={contactLinkDef}
        >
          {contactLink.url && (
            <div className="mt-8">
              <a
                href={contactLink.url}
                target={contactLink._target || '_self'}
                className="font-body text-primary underline"
              >
                {contactLink.label || 'Contact Us'}
              </a>
            </div>
          )}
        </EditableSlotGroup>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AccessibilityStatement',
  displayName: 'Accessibility Statement',
  tokens: {
    'accessibilitystatement.heading': {
      label: 'Page Heading',
      type: 'text',
      defaultValue: 'Accessibility Statement',
    },
    'accessibilitystatement.body': {
      label: 'Accessibility Statement',
      type: 'richtext',
      defaultValue:
        '<p>We are committed to ensuring digital accessibility for people with disabilities. We continually improve the user experience for everyone and apply relevant accessibility standards.</p>' +
        '<p>This website conforms to the <strong>Web Content Accessibility Guidelines (WCAG) 2.1 Level AA</strong>. These guidelines explain how to make web content more accessible to people with disabilities.</p>' +
        '<p>We welcome your feedback on the accessibility of this site. If you experience any barriers or have suggestions for improvement, please let us know.</p>',
    },
  },
  slotGroups: {
    'accessibilitystatement.contact_link': contactLinkDef,
  },
};

(AccessibilityStatement as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default AccessibilityStatement;
